/**
 * A charge, as a receipt.
 *
 * "Member activity, would be nice if clicking description would also
 * share receipt (later PDF's get saved). Show timestamp of transaction
 * if that detail is available."
 *
 * The timestamp was always available. `ar_transaction` has carried
 * `captured_at` — when the member ordered — and `posted_at` — when the
 * ledger recorded it — since migration 0004, and the difference between
 * them is deliberate: a beverage cart that syncs at nine in the evening
 * still lands on the right day. It also carries `charged_by_person_id`,
 * which is who signed. None of it has ever reached a screen, so a member
 * disputing a charge got a date and a total.
 *
 * ---------------------------------------------------------------------
 * WHAT A RECEIPT IS FOR
 *
 * Not "what did this cost" — the row already says that. It is for the
 * three questions asked when a member rings up about a line:
 *
 *   WHO. `charged_by_person_id`. The answer is usually "your daughter,
 *   at the pool", and it ends the call.
 *   WHEN. To the minute, and with the ordered/posted distinction shown
 *   when they differ, because "I was not there on the 4th" is answered
 *   by "it was ordered on the 3rd at 19:40 and posted overnight".
 *   WHAT. The lines, which have always been here, plus the outlet and
 *   the department, which have not.
 *
 * And it prints. A club that cannot hand a member a piece of paper at
 * the desk ends up photographing a screen.
 */

import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date, dateTime, money, titleCase } from '../lib/format.ts';
import { ErrorBanner, Loading, Status } from './ui.tsx';

interface Line {
  line_number: number;
  description: string;
  quantity: string;
  unit_amount_minor: number;
  extended_minor: number;
  tax_minor: number;
  revenue_category: string | null;
}

interface Txn {
  id: string;
  txn_type: string;
  amount_minor: number;
  tax_minor: number;
  gratuity_minor: number;
  accounting_date: string;
  captured_at: string;
  posted_at: string;
  description: string | null;
  source_module: string;
  source_reference: string | null;
  statement_id: string | null;
  statement_number: string | null;
  statement_period_end: string | null;
  reverses_txn_id: string | null;
  charged_by_person_id: string | null;
  charged_by_name: string | null;
  outlet_name: string | null;
  outlet_code: string | null;
  department_name: string | null;
  account_number: string;
  account_name: string;
}

interface Reversal {
  id: string;
  accounting_date: string;
  amount_minor: number;
  description: string | null;
  relation: 'reverses' | 'reversed_by';
}

/** Same instant to the minute? Then there is nothing to explain. */
function sameMinute(a: string, b: string): boolean {
  return a.slice(0, 16) === b.slice(0, 16);
}

export function Receipt({
  accountId,
  txnId,
  onClose,
}: {
  accountId: string;
  txnId: string;
  onClose: () => void;
}) {
  const { currency, club } = useSession();
  const q = useApi<{ transaction: Txn; lines: Line[]; reversal: Reversal | null }>(
    `/v1/member-accounts/${accountId}/transactions/${txnId}`,
    [accountId, txnId],
  );

  if (q.loading) return <Loading rows={4} />;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (!q.data) return null;

  const t = q.data.transaction;
  const lines = q.data.lines;
  const net = Number(t.amount_minor) - Number(t.tax_minor) - Number(t.gratuity_minor);

  return (
    <div className="receipt">
      <div className="receipt__head">
        <div>
          <div className="receipt__club">{club?.legal_name ?? 'The club'}</div>
          <strong>{t.description ?? titleCase(t.txn_type)}</strong>
          <div className="muted small">
            {t.account_name} · <span className="mono">{t.account_number}</span>
          </div>
        </div>
        <div className="spacer" />
        <div className="actions receipt__actions">
          <button className="secondary small" onClick={() => window.print()}>
            Print
          </button>
          <button className="secondary small" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {t.reverses_txn_id && (
        <Status tone="warning">This is a reversal — it undoes an earlier charge.</Status>
      )}
      {q.data.reversal?.relation === 'reversed_by' && (
        <Status tone="critical">
          Reversed on {date(q.data.reversal.accounting_date)}. Nothing is owed on this line.
        </Status>
      )}

      <dl className="receipt__facts">
        <div>
          <dt>Ordered</dt>
          <dd>{dateTime(t.captured_at)}</dd>
        </div>
        {/* Only when they differ. On the ordinary charge they are the
            same instant and printing both invites the question "why are
            there two times" for no reason. */}
        {!sameMinute(t.captured_at, t.posted_at) && (
          <div>
            <dt>Posted</dt>
            <dd>
              {dateTime(t.posted_at)}
              <span className="muted small"> · when the ledger recorded it</span>
            </dd>
          </div>
        )}
        {t.accounting_date !== t.captured_at.slice(0, 10) && (
          <div>
            <dt>Billed to</dt>
            <dd>{date(t.accounting_date)}</dd>
          </div>
        )}
        <div>
          <dt>Signed by</dt>
          <dd>
            {t.charged_by_name ?? (
              <span className="muted">
                not recorded — posted by the office rather than at an outlet
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>
            {t.outlet_name ?? titleCase(t.source_module)}
            {t.department_name && <span className="muted small"> · {t.department_name}</span>}
          </dd>
        </div>
        {t.source_reference && (
          <div>
            <dt>Reference</dt>
            <dd className="mono small">{t.source_reference}</dd>
          </div>
        )}
        {t.statement_id && (
          <div>
            <dt>Statement</dt>
            <dd>
              {t.statement_number ?? 'Swept onto a statement'}
              {t.statement_period_end && (
                <span className="muted small"> · to {date(t.statement_period_end)}</span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {lines.length > 0 && (
        <table className="data receipt__lines">
          <thead>
            <tr>
              <th style={{ width: 60 }} className="num">
                Qty
              </th>
              <th>Item</th>
              <th className="num" style={{ width: 110 }}>
                Each
              </th>
              <th className="num" style={{ width: 110 }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_number}>
                <td className="num">{Number(l.quantity)}</td>
                <td>
                  {l.description}
                  {l.revenue_category && (
                    <span className="muted small"> · {titleCase(l.revenue_category)}</span>
                  )}
                </td>
                <td className="num muted">{money(l.unit_amount_minor, currency)}</td>
                <td className="num">{money(l.extended_minor, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="receipt__total">
        {/* Broken out even when tax is zero on a club that includes it in
            the price: a member reading a total wants to know whether the
            tax is in it, and a missing line does not say either way. */}
        {lines.length > 0 && (
          <div>
            <span>Net</span>
            <span className="num">{money(net, currency)}</span>
          </div>
        )}
        {Number(t.tax_minor) !== 0 && (
          <div>
            <span>Tax</span>
            <span className="num">{money(t.tax_minor, currency)}</span>
          </div>
        )}
        {Number(t.gratuity_minor) !== 0 && (
          <div>
            <span>Gratuity</span>
            <span className="num">{money(t.gratuity_minor, currency)}</span>
          </div>
        )}
        <div className="receipt__grand">
          <span>{Number(t.amount_minor) < 0 ? 'Credited' : 'Charged'}</span>
          <span className="num">{money(Math.abs(Number(t.amount_minor)), currency)}</span>
        </div>
      </div>
    </div>
  );
}
